import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '@/store/store';
import type { Segment, SegmentCategory } from '@interfaces/segments/domain';
import { segmentsAdapter, segmentCategoriesAdapter } from './segmentsSlice';
import { selectSelectedMarketIds } from '@/store/slices/userSelection/selectors';

const segsSelectors = segmentsAdapter.getSelectors(
  (s: RootState) => s.activeGroup.segments.segments,
);
const catsSelectors = segmentCategoriesAdapter.getSelectors(
  (s: RootState) => s.activeGroup.segments.categories,
);

export const selectAllSegments = segsSelectors.selectAll;
export const selectAllSegmentCategories = catsSelectors.selectAll;
export const selectSegmentByCode = segsSelectors.selectById;
export const selectSegmentCategoryByCode = catsSelectors.selectById;

function hasMarketOverlap(a: number[], b: number[]): boolean {
  if (b.length === 0) return true;
  return a.some((m) => b.includes(m));
}

export const selectSegmentsForMarkets = createSelector(
  [selectAllSegments, selectSelectedMarketIds],
  (segments, markets) =>
    segments.filter(
      (s) => s.type === 'SYSTEM' || hasMarketOverlap(s.markets, markets),
    ),
);

export const selectAppliedSegmentCodes = createSelector(
  [selectAllSegments],
  (segments) => segments.filter((s) => s.isApplied).map((s) => s.code),
);

export interface SegmentPickerSection {
  category: SegmentCategory;
  segments: Segment[];
}

export const selectSegmentPickerData = createSelector(
  [selectAllSegmentCategories, selectSegmentsForMarkets],
  (categories, segments): SegmentPickerSection[] => {
    const byCategory = new Map<string, Segment[]>();
    for (const s of segments) {
      const arr = byCategory.get(s.categoryCode) ?? [];
      arr.push(s);
      byCategory.set(s.categoryCode, arr);
    }
    return categories
      .map((c) => ({ category: c, segments: byCategory.get(c.code) ?? [] }))
      .filter((sec) => sec.segments.length > 0);
  },
);
